import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import CloudDownloadIcon from '@material-ui/icons/CloudDownload'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import EditIcon from '@material-ui/icons/Edit'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import LockIcon from '@material-ui/icons/Lock'
import LockOpenIcon from '@material-ui/icons/LockOpen'
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera'
import SaveIcon from '@material-ui/icons/Save'
import html2canvas from 'html2canvas'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react'

import {
  deleteNewsletterDraft,
  faceCropEventCreativePhoto,
  fetchScopedCentres,
  fetchNewsletterDraft,
  fetchNewsletterDrafts,
  freezeNewsletterDraft,
  generateEventCreativeImage,
  saveNewsletterDraft,
  updateNewsletterDraft
} from 'api'
import constants from 'app/constants'
import { showToast } from 'app/helpers'
import ohLogo from 'assets/images/newsletter/oh_logo.svg'

// Persist the picked centre across visits (mirrors Newsletter).
const CENTRE_KEY = 'eventcreative.centre'

// The list's default centre filter: everything. The API takes this literal
// string ("centre=all"), so it doubles as the Select's value.
const ALL_CENTRES = 'all'

// The format comes from the design file (Creatives sample): every frame there is
// 2160x2700 — i.e. 1080x1350 at 2x, 4:5 portrait. The old 1080x1080 square was
// the wrong shape for everything we post. Exports as a PNG for WhatsApp / IG.
const BOARD_W = 1080
const BOARD_H = 1350
const ORANGE = '#ef5d34'
const INK = '#39393B'
const WHITE = '#ffffff'

// Optional sparkle bullets (the richer playdate posters use them; the master
// template does not). The first word of each takes the next colour in the cycle.
// NOTE: the brand font ships Regular/Medium/Demi only — there is NO 700 weight.
// Asking for one makes the browser synthesise a fake bold that rasterises badly
// through html2canvas, so 600 is as heavy as this poster honestly goes.
const BULLET_COLORS = ['#4E9A6A', '#E3A81E', '#4A90C4', ORANGE]

const COMM_TYPE = 'event'

// Event-creative categories (senior's taxonomy, 2026-07-10). Keys are stored
// on the draft row (backend `category` column) so the list can show/group them.
const CATEGORIES = [
  { key: 'celebration', label: 'Celebration Wishes' },
  { key: 'oh_event', label: 'Openhouse Event Invitation' },
  { key: 'field_trip', label: 'Field Trip' },
  { key: 'collab', label: 'External Collaboration Invitation' }
]
const categoryLabel = (key) =>
  CATEGORIES.find((c) => c.key === key)?.label || '—'

// Sub-type narrows the category. It is not just a label: it is the strongest
// signal for what the artwork should show (a "Fire station" trip and a "Diwali"
// wish want completely different images), and it lets the list group past work.
// 'other' keeps staff unblocked when reality outruns the list.
const SUB_TYPES = {
  celebration: [
    'Diwali',
    'Holi',
    'Christmas',
    'New Year',
    'Eid',
    'Sankranti / Pongal',
    'Onam',
    'Independence Day',
    'Republic Day',
    "Mother's Day",
    "Father's Day",
    "Children's Day",
    "Teacher's Day",
    "Grandparents' Day",
    'Earth Day'
  ],
  oh_event: [
    'Annual Day',
    'Sports Day',
    'Showcase',
    'Museum Day',
    'Orientation / Open House',
    'Parent-Teacher Meeting',
    'Workshop',
    'Playdate',
    'Movie Night',
    'Summer Camp',
    'Graduation'
  ],
  field_trip: [
    'Park',
    'Bookstore',
    'Fire Station',
    'Museum',
    'Zoo',
    'Farm',
    'Bakery',
    'Post Office',
    'Police Station',
    'Planetarium',
    'Aquarium',
    'Botanical Garden'
  ],
  collab: [
    'Brand Partnership',
    'Guest Host / Founding Parent',
    'Restaurant or Café',
    'Studio or Class',
    'Community Event'
  ]
}
const subTypesFor = (category) => SUB_TYPES[category] || []

// The creatives genuinely use both: the design-file master template is real
// photography (Bear Hunt, Robotics), while the pizza / movie-night posters are
// watercolour cartoon. Category picks the default; staff can override.
const IMAGE_STYLES = [
  { key: 'photo', label: 'Photo (real children)' },
  { key: 'illustration', label: 'Illustration (watercolour)' }
]
const defaultStyleFor = (category) =>
  category === 'celebration' || category === 'collab' ? 'illustration' : 'photo'

// ONE structure for every creative (confirmed by the team, 2026-07-12): the
// wordmark top-right, the copy on white, and the photo/artwork ALWAYS at the
// bottom. The photo-on-top variant from the design file is not used — even
// field trips put the photo below.
//
// What differs is the TEMPLATE, i.e. which extra blocks exist:
//   standard    — nothing extra.            Celebration, Openhouse Event, Field Trip
//   partner     — + partner logo, top-left. External Collaboration (Brik Oven)
//   facilitator — + facilitator band WITH A PHOTO. The only template with one.
//                                            (DoughReMom, Dino Playdate)
const TEMPLATES = [
  { key: 'standard', label: 'Standard (no partner, no facilitator)' },
  { key: 'partner', label: 'External partner (logo top-left)' },
  { key: 'facilitator', label: 'Facilitator (photo, name, bio)' }
]

// Which templates a category may use. Celebrations and field trips have neither
// a partner nor a host, so those inputs never render for them — a wrong creative
// cannot be built.
const TEMPLATES_FOR = {
  celebration: ['standard'],
  oh_event: ['standard', 'facilitator'],
  field_trip: ['standard'],
  collab: ['standard', 'partner', 'facilitator']
}
const templatesFor = (category) => TEMPLATES_FOR[category] || ['standard']
const defaultTemplateFor = (category) =>
  category === 'collab' ? 'partner' : 'standard'

// The title can carry an accent colour — "Chit-Chaat" is green, "MOVIE NIGHT"
// orange — while the eyebrow above it stays dark.
const TITLE_COLORS = [
  { key: '', label: 'Default (dark)' },
  { key: ORANGE, label: 'Orange' },
  { key: '#4E9A6A', label: 'Green' },
  { key: '#C0392B', label: 'Red' },
  { key: '#4A90C4', label: 'Blue' }
]

// "13/07/2026, 14:10" — no seconds. A roster of drafts is not a stopwatch; the
// second-precision timestamps just made the column noisy and wide.
const lastUpdated = (iso) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : '—'

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    display: 'flex',
    gap: theme.spacing(3),
    alignItems: 'flex-start',
    height: 'calc(100vh - 64px)',
    boxSizing: 'border-box'
  },
  listRoot: { padding: theme.spacing(3) },
  listHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
    flexWrap: 'wrap'
  },
  empty: {
    padding: theme.spacing(6),
    textAlign: 'center',
    color: theme.palette.text.secondary
  },
  formColumn: {
    width: 430,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  form: {
    flex: 1,
    overflowY: 'auto',
    paddingRight: theme.spacing(1)
  },
  formSection: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  sectionTitle: { fontWeight: 600, marginBottom: theme.spacing(1.5) },
  row: { display: 'flex', gap: theme.spacing(1), alignItems: 'center' },
  field: { marginBottom: theme.spacing(1.5) },
  photoThumb: {
    width: 56,
    height: 56,
    objectFit: 'cover',
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`
  },
  previewPane: {
    flex: 1,
    height: '100%',
    overflow: 'auto',
    backgroundColor: theme.palette.grey[200],
    borderRadius: 8,
    position: 'relative'
  },
  previewScaler: { transformOrigin: 'top left' },
  // Mounted, laid out and painted so html2canvas can read it — just never
  // visible. NOT display:none, which would leave nothing to capture.
  hiddenExport: {
    position: 'fixed',
    top: 0,
    left: -99999,
    zIndex: -1,
    pointerEvents: 'none'
  },

  /* ------------------------- artboard (preview) -------------------------
     The house template, straight off the design file (Creatives sample,
     2160x2700 = 1080x1350 at 2x): a full-bleed photo across the top ~55% with
     the wordmark reversed out on it, then a white block with a centred bold
     title, a subtitle paragraph, the date/ages line and the venue. Bullets and
     the footnote are optional — the richer playdate posters use them, the
     master template does not. */
  board: {
    width: BOARD_W,
    height: BOARD_H,
    backgroundColor: WHITE,
    fontFamily: 'AvenirRounded, Avenir, sans-serif',
    color: INK,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  // The artwork always sits at the bottom and fills whatever the copy leaves.
  // Backed in WHITE, not a tint: any tinted backing shows as a hard band wherever
  // the artwork does not reach.
  artWrap: {
    position: 'relative',
    width: '100%',
    flex: '1 1 auto',
    minHeight: 0,
    backgroundColor: WHITE,
    overflow: 'hidden'
  },
  // The artwork butts straight up against the copy, so ANY off-white in the
  // generation reads as a seam across the poster. Fading the top edge into white
  // dissolves it, and the illustrations are asked for a pure-white ground so the
  // two whites meet. A gradient overlay rather than a CSS mask: html2canvas
  // rasterises gradients reliably and masks unreliably.
  artFade: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 130,
    background: `linear-gradient(to bottom, ${WHITE} 0%, rgba(255,255,255,0) 100%)`,
    pointerEvents: 'none'
  },
  // Orange wordmark on white. Centred on its own — space-between would shove the
  // lone wordmark to the left edge, which is exactly what it used to do.
  headRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    width: '100%',
    padding: '56px 76px 0',
    boxSizing: 'border-box',
    flex: '0 0 auto'
  },
  // With a partner mark the two bookend each other: partner left, wordmark right.
  headRowPartner: { justifyContent: 'space-between' },
  headLogo: { width: 230, display: 'block' },
  headTagline: {
    fontSize: 23,
    fontStyle: 'italic',
    color: ORANGE,
    margin: '4px 0 0',
    textAlign: 'right'
  },
  // The partner logo is a PEER of the Openhouse wordmark, so it is sized like
  // one: same 230px width. It used to be capped at 92px tall, which quietly
  // shrank any logo that wasn't a wide, short wordmark (a square or stacked
  // mark hit the height cap first and came out tiny next to ours).
  coBrand: {
    width: 230,
    maxHeight: 130,
    objectFit: 'contain',
    display: 'block'
  },
  photo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block'
  },
  photoEmpty: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#a79f95',
    fontSize: 30
  },
  // Reversed-out wordmark, sitting on the photo (filter beats shipping a
  // second, white copy of the same SVG).
  logo: {
    position: 'absolute',
    top: 46,
    right: 64,
    width: 190,
    // Reversed out white — and given a soft shadow, because a bright patch of
    // photo behind it (a window, a wall) otherwise swallows it whole.
    filter: 'brightness(0) invert(1) drop-shadow(0 2px 10px rgba(0,0,0,0.45))',
    opacity: 0.98
  },
  // The white half. Clips instead of pushing content off the board.
  // The copy NEVER shrinks (flex-shrink 0). It used to be '0 1 auto' with
  // overflow:hidden, so a long copy block was squeezed and its last line — the
  // footnote — was sliced in half by the artwork below it. The artwork is the
  // flexible one: it takes whatever height the copy leaves. Long copy now means
  // a smaller picture, not a mangled footnote.
  boardBody: {
    flex: '0 0 auto',
    padding: '44px 70px 34px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center'
  },
  // "Community event" sits above "Chit-Chaat" / "MOVIE NIGHT" — always dark,
  // even when the title below it takes an accent colour.
  eyebrow: {
    fontSize: 42,
    fontWeight: 500,
    lineHeight: 1.15,
    color: INK,
    margin: '0 0 4px',
    textTransform: 'lowercase'
  },
  eventTitle: {
    fontSize: 68,
    fontWeight: 600,
    lineHeight: 1.14,
    letterSpacing: '-0.5px',
    color: INK,
    margin: 0,
    textTransform: 'lowercase'
  },
  // "Play Beyond Toys  with  [DoughReMom logo]" — the partner mark set into the
  // title line rather than locked up in the corner.
  titleWithRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 26,
    margin: '14px 0 0'
  },
  titleWithWord: {
    fontSize: 50,
    fontWeight: 600,
    textTransform: 'lowercase'
  },
  partnerInline: { maxWidth: 300, maxHeight: 130, objectFit: 'contain' },

  /* facilitator band — "FACILITATED BY" (DoughReMom, Dino Playdate) */
  hostBand: {
    flex: '0 0 auto',
    backgroundColor: '#fbe3d6',
    padding: '40px 80px 46px',
    textAlign: 'center'
  },
  hostRule: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 18,
    color: ORANGE,
    fontSize: 21,
    fontWeight: 600,
    letterSpacing: 3,
    textTransform: 'uppercase'
  },
  hostRuleLine: { width: 66, height: 2, backgroundColor: ORANGE },
  hostRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 40,
    marginTop: 22,
    textAlign: 'left'
  },
  hostPhoto: {
    width: 210,
    height: 210,
    flexShrink: 0,
    borderRadius: '50%',
    objectFit: 'cover',
    border: `7px solid ${WHITE}`,
    boxShadow: `0 0 0 5px ${ORANGE}`
  },
  hostPhotoEmpty: {
    width: 210,
    height: 210,
    flexShrink: 0,
    borderRadius: '50%',
    border: `4px dashed ${ORANGE}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: ORANGE,
    fontSize: 24
  },
  hostInfo: { flex: 1, minWidth: 0 },
  hostName: { fontSize: 48, fontWeight: 600, margin: 0 },
  hostRole: {
    fontSize: 25,
    fontWeight: 600,
    fontStyle: 'italic',
    color: ORANGE,
    margin: '5px 0 0'
  },
  hostBio: {
    fontSize: 23,
    lineHeight: '33px',
    margin: '12px 0 0',
    whiteSpace: 'pre-wrap'
  },
  subtitle: {
    fontSize: 30,
    fontWeight: 400,
    lineHeight: '42px',
    color: '#4c4c4e',
    margin: '22px 0 0'
  },
  bullets: { margin: '24px 0 0', width: '100%' },
  bulletRow: {
    fontSize: 28,
    lineHeight: '46px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'baseline',
    gap: 10
  },
  sparkle: { color: ORANGE, fontSize: 21 },
  bulletLead: { fontWeight: 600 },
  metaLine: {
    fontSize: 29,
    fontWeight: 600,
    margin: '30px 0 0',
    textTransform: 'lowercase'
  },
  venue: {
    fontSize: 29,
    fontWeight: 500,
    lineHeight: '40px',
    margin: '12px 0 0',
    whiteSpace: 'pre-wrap',
    textTransform: 'lowercase'
  },
  footnote: {
    fontSize: 25,
    fontWeight: 600,
    fontStyle: 'italic',
    color: ORANGE,
    margin: '20px 0 0',
    textTransform: 'lowercase'
  }
}))

const MAX_PHOTO_DIM = 1400

const readFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const img = new Image()
      img.onload = () => {
        const ratio = Math.min(
          1,
          MAX_PHOTO_DIM / Math.max(img.width, img.height)
        )
        if (ratio === 1 && file.size < 400 * 1024) {
          resolve(reader.result)
          return
        }
        const canvas = document.createElement('canvas')
        canvas.width = Math.round(img.width * ratio)
        canvas.height = Math.round(img.height * ratio)
        canvas
          .getContext('2d')
          .drawImage(img, 0, 0, canvas.width, canvas.height)
        resolve(canvas.toDataURL('image/jpeg', 0.85))
      }
      img.onerror = reject
      img.src = reader.result
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })

// Each bullet is one typed line; its FIRST word takes the accent colour, the
// way the shipped posters do it ("**Dig** for hidden fossils in the sand pit").
const parseBullets = (text) =>
  (text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, i) => {
      const [lead, ...rest] = line.split(' ')
      return {
        lead,
        rest: rest.join(' '),
        color: BULLET_COLORS[i % BULLET_COLORS.length]
      }
    })

const EventCreative = () => {
  const classes = useStyles()

  const [centres, setCentres] = useState([])
  // False once the user is mapped to a centre — "all centres" then means nothing
  // to them, so the option is not offered.
  const [unrestricted, setUnrestricted] = useState(true)
  // centreUuid = the centre THIS creative belongs to (what it saves against).
  // listCentre = what the LIST is filtered by; opens on every centre.
  const [listCentre, setListCentre] = useState(ALL_CENTRES)
  const [centreUuid, setCentreUuid] = useState(
    () => window.localStorage.getItem(CENTRE_KEY) || ''
  )

  const [title, setTitle] = useState('')
  const [withLine, setWithLine] = useState('')
  const [subtitle, setSubtitle] = useState('')
  const [category, setCategory] = useState('oh_event')
  const [subType, setSubType] = useState('')
  const [ages, setAges] = useState('')
  const [dateLine, setDateLine] = useState('')
  const [venue, setVenue] = useState('')
  const [body, setBody] = useState('')
  const [footnote, setFootnote] = useState('')
  const [photo, setPhoto] = useState('')
  const [imageStyle, setImageStyle] = useState('photo')
  const [template, setTemplate] = useState('standard')
  const [coBrand, setCoBrand] = useState('')
  const [eyebrow, setEyebrow] = useState('')
  const [titleColor, setTitleColor] = useState('')
  const [hostName, setHostName] = useState('')
  const [hostRole, setHostRole] = useState('')
  const [hostBio, setHostBio] = useState('')
  const [hostPhoto, setHostPhoto] = useState('')
  const [generating, setGenerating] = useState(false)
  const [copying, setCopying] = useState(false)
  // The prompt, shown in a dialog. Copying straight from the network callback
  // does not work: navigator.clipboard.writeText() needs the user's click to
  // still be "active", and the round-trip to build the prompt outlives that
  // activation, so the write was rejected and the toast blamed the prompt. Show
  // it, and let the Copy button in the dialog do the writing on its own click —
  // and if even that is refused, the text is right there to select.
  const [promptText, setPromptText] = useState('')
  const [cropping, setCropping] = useState(false)

  const [downloading, setDownloading] = useState(false)
  const [infoOpen, setInfoOpen] = useState(false)
  const [view, setView] = useState('list')
  const [drafts, setDrafts] = useState([])
  const [draftUuid, setDraftUuid] = useState('')
  const [savingDraft, setSavingDraft] = useState(false)
  const [loadingDraft, setLoadingDraft] = useState(false)
  const [autoDownload, setAutoDownload] = useState(false)

  const boardRef = useRef(null)
  const paneRef = useRef(null)
  const scalerRef = useRef(null)
  const [scale, setScale] = useState(0.5)
  const photoInputRef = useRef(null)
  const coBrandInputRef = useRef(null)
  const hostPhotoInputRef = useRef(null)

  useEffect(() => {
    let active = true
    ;(async () => {
      try {
        const data = await fetchScopedCentres()
        const list = data?.results || data || []
        if (!active) return
        setCentres(list)
        setUnrestricted(Boolean(data?.unrestricted))
        setCentreUuid((prev) =>
          prev && list.some((c) => c.uuid === prev) ? prev : list[0]?.uuid || ''
        )
      } catch (e) {
        showToast('Could not load centres', constants.ERROR)
      }
    })()
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    if (centreUuid) window.localStorage.setItem(CENTRE_KEY, centreUuid)
  }, [centreUuid])

  useLayoutEffect(() => {
    const fit = () => {
      const pane = paneRef.current
      const board = boardRef.current
      const scaler = scalerRef.current
      if (!pane || !board || !scaler) return
      const s = Math.min(1, (pane.clientWidth - 32) / BOARD_W)
      setScale(s)
      scaler.style.height = `${board.offsetHeight * s + 32}px`
    }
    fit()
    window.addEventListener('resize', fit)
    return () => window.removeEventListener('resize', fit)
  }, [view])

  const centre = centres.find((c) => c.uuid === centreUuid)
  const centreName = centre?.name || centre?.centre_name || ''

  /* -------------------------------- drafts ------------------------------- */

  // Same store as the newsletter, filtered to comm_type='event' (the
  // newsletter list shows the rest).
  const loadDrafts = useCallback(async () => {
    if (!listCentre) return
    try {
      const rows = await fetchNewsletterDrafts(listCentre)
      setDrafts(
        (Array.isArray(rows) ? rows : []).filter(
          (d) => d.comm_type === COMM_TYPE
        )
      )
    } catch (e) {
      showToast('Could not load saved creatives', constants.ERROR)
    }
  }, [centreUuid])

  useEffect(() => {
    setDraftUuid('')
    loadDrafts()
  }, [loadDrafts])

  const collectData = () => ({
    version: 2,
    title,
    withLine,
    subtitle,
    category,
    subType,
    ages,
    dateLine,
    venue,
    body,
    footnote,
    photo,
    imageStyle,
    template,
    coBrand,
    eyebrow,
    titleColor,
    hostName,
    hostRole,
    hostBio,
    hostPhoto
  })

  // v1 drafts (square board: title/dateLine/venue/body/photo/borderStyle) still
  // open — the fields it never had simply come up empty. borderStyle is dropped:
  // no creative in the design file has border bands.
  const applyData = (d) => {
    setTitle(d.title || '')
    setWithLine(d.withLine || '')
    setSubtitle(d.subtitle || '')
    setCategory(d.category || 'oh_event')
    setSubType(d.subType || '')
    setAges(d.ages || '')
    setDateLine(d.dateLine || '')
    setVenue(d.venue || '')
    setBody(d.body || '')
    setFootnote(d.footnote || '')
    setPhoto(d.photo || '')
    const style = d.imageStyle || defaultStyleFor(d.category || 'oh_event')
    setImageStyle(style)
    setTemplate(d.template || defaultTemplateFor(d.category || 'oh_event'))
    setCoBrand(d.coBrand || '')
    setEyebrow(d.eyebrow || '')
    setTitleColor(d.titleColor || '')
    setHostName(d.hostName || '')
    setHostRole(d.hostRole || '')
    setHostBio(d.hostBio || '')
    setHostPhoto(d.hostPhoto || '')
  }

  const saveDraft = async () => {
    if (!centreUuid) return
    const name = title.trim() || 'Untitled event'
    setSavingDraft(true)
    try {
      const saved = draftUuid
        ? await updateNewsletterDraft(draftUuid, {
            name,
            data: collectData(),
            category
          })
        : await saveNewsletterDraft({
            centre: centreUuid,
            name,
            data: collectData(),
            commType: COMM_TYPE,
            category
          })
      setDraftUuid(saved.uuid)
      showToast(`Creative "${saved.name}" saved`, constants.SUCCESS)
      loadDrafts()
    } catch (e) {
      showToast('Could not save the creative', constants.ERROR)
    } finally {
      setSavingDraft(false)
    }
  }

  // Back used to just leave — and everything typed since the last Save went
  // with it. Save on the way out. A creative with no title is not work, it is
  // an empty form, so that one leaves without saving (and without littering the
  // list with another "Untitled event").
  const handleBack = async () => {
    if (openIsFrozen || !centreUuid || !title.trim()) {
      setView('list')
      return
    }
    await saveDraft()
    setView('list')
  }

  const artworkArgs = () => ({
    title: title.trim(),
    category,
    // The sub-type is the strongest hint the image service gets — "Fire
    // Station" vs "Diwali" matters far more than the category does.
    subType,
    style: imageStyle,
    details: [subtitle.trim(), body.trim()].filter(Boolean).join('\n')
  })

  // Image generation has no free tier on any provider, so until billing is live
  // this is the working path: copy the prompt, paste it into the Gemini app
  // (which generates images free for a premium account), upload the result.
  const handleCopyPrompt = async () => {
    setCopying(true)
    try {
      const data = await generateEventCreativeImage({
        ...artworkArgs(),
        promptOnly: true
      })
      const text = data?.prompt || ''
      if (!text) {
        showToast('Could not build the prompt', constants.ERROR)
        return
      }
      setPromptText(text)
    } catch (e) {
      showToast(
        e?.response?.data?.detail || 'Could not build the prompt',
        constants.ERROR
      )
    } finally {
      setCopying(false)
    }
  }

  // Fired by the dialog's own button, so the click is still active and the
  // clipboard write is allowed. execCommand is the fallback for browsers that
  // refuse the async API outright.
  const copyPromptToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(promptText)
      showToast('Prompt copied — paste it into Gemini', constants.SUCCESS)
      return
    } catch (e) {
      /* fall through to the legacy path */
    }
    try {
      const ta = document.createElement('textarea')
      ta.value = promptText
      ta.style.position = 'fixed'
      ta.style.left = '-9999px'
      document.body.appendChild(ta)
      ta.select()
      const ok = document.execCommand('copy')
      document.body.removeChild(ta)
      showToast(
        ok
          ? 'Prompt copied — paste it into Gemini'
          : 'Could not copy — select the text and copy it by hand',
        ok ? constants.SUCCESS : constants.ERROR
      )
    } catch (e) {
      showToast(
        'Could not copy — select the text and copy it by hand',
        constants.ERROR
      )
    }
  }

  // AI artwork for the photo slot — needs the server-side image API key
  // (503 with a clear message until it's configured).
  const handleGenerateArtwork = async () => {
    setGenerating(true)
    try {
      const data = await generateEventCreativeImage({
        title: title.trim(),
        category,
        // The sub-type is the strongest hint the image service gets — "Fire
        // Station" vs "Diwali" matters far more than the category does.
        subType,
        details: [subtitle.trim(), body.trim()].filter(Boolean).join('\n')
      })
      if (data?.image) {
        setPhoto(data.image)
        showToast(
          'Artwork generated — placed in the photo slot',
          constants.SUCCESS
        )
      } else {
        showToast('The image service returned nothing', constants.ERROR)
      }
    } catch (e) {
      showToast(
        e?.response?.data?.detail || 'Could not generate the artwork',
        constants.ERROR
      )
    } finally {
      setGenerating(false)
    }
  }

  const openNew = () => {
    setDraftUuid('')
    applyData({})
    // A creative belongs to ONE centre. With the list on "all" there is nothing
    // to inherit, so the builder's picker has to ask (Save stays disabled until
    // it is answered).
    setCentreUuid(listCentre === ALL_CENTRES ? '' : listCentre)
    setView('builder')
  }

  const openDraft = async (uuid, { download = false } = {}) => {
    setDraftUuid(uuid)
    setLoadingDraft(true)
    try {
      const draft = await fetchNewsletterDraft(uuid)
      applyData(draft?.data || {})
      // The list may be showing every centre, so the filter is NOT this
      // creative's centre — the draft is.
      if (draft?.centre_uuid) setCentreUuid(draft.centre_uuid)
      // Download from the list must not open the editor: the board renders
      // off-screen on the list and is captured there.
      if (download) {
        setAutoDownload(true)
        return
      }
      setView('builder')
    } catch (e) {
      showToast('Could not load the creative', constants.ERROR)
    } finally {
      setLoadingDraft(false)
    }
  }

  const removeDraft = async (uuid) => {
    const current = drafts.find((d) => d.uuid === uuid)
    if (!window.confirm(`Delete "${current?.name || 'this creative'}"?`)) return
    try {
      await deleteNewsletterDraft(uuid)
      if (uuid === draftUuid) setDraftUuid('')
      showToast('Creative deleted', constants.SUCCESS)
      loadDrafts()
    } catch (e) {
      showToast('Could not delete the creative', constants.ERROR)
    }
  }

  const freezeDraft = async (uuid) => {
    const current = drafts.find((d) => d.uuid === uuid)
    if (
      !window.confirm(
        `Freeze "${current?.name || 'this creative'}"? A frozen creative can ` +
          'never be edited or deleted again.'
      )
    )
      return
    try {
      await freezeNewsletterDraft(uuid)
      showToast('Creative frozen', constants.SUCCESS)
      loadDrafts()
    } catch (e) {
      showToast('Could not freeze the creative', constants.ERROR)
    }
  }

  const openIsFrozen = !!drafts.find((d) => d.uuid === draftUuid)?.frozen

  /* --------------------------------- PNG --------------------------------- */

  // The whole point of this page: a shareable image, not a PDF.
  const downloadPng = async () => {
    const board = boardRef.current
    const scaler = scalerRef.current
    if (!board || !scaler) return
    setDownloading(true)
    const prevTransform = scaler.style.transform
    scaler.style.transform = 'none'
    try {
      // Brand fonts must be fully loaded before rasterising, or html2canvas
      // bakes the fallback font into the PNG (font-display: swap).
      if (document.fonts && document.fonts.ready) await document.fonts.ready
      const canvas = await html2canvas(board, {
        scale: 2,
        backgroundColor: WHITE,
        logging: false
      })
      const link = document.createElement('a')
      const name = [centreName, title.trim() || 'event']
        .filter(Boolean)
        .join(' - ')
      link.download = `${name}.png`
      link.href = canvas.toDataURL('image/png')
      link.click()
      showToast(`Saved "${link.download}" to your Downloads`, constants.SUCCESS)
    } catch (e) {
      showToast('Could not generate the image', constants.ERROR)
    } finally {
      scaler.style.transform = prevTransform
      setDownloading(false)
    }
  }

  useEffect(() => {
    if (!autoDownload) return undefined
    const t = setTimeout(async () => {
      try {
        await downloadPng()
      } finally {
        setAutoDownload(false)
      }
    }, 800)
    return () => clearTimeout(t)
  }, [autoDownload])

  /* --------------------------------- views -------------------------------- */

  // The LIST's filter — opens on every centre.
  const listCentrePicker = (
    <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
      <InputLabel>Centre</InputLabel>
      <Select
        label="Centre"
        value={listCentre}
        onChange={(e) => setListCentre(e.target.value)}
      >
        {unrestricted && <MenuItem value={ALL_CENTRES}>All centres</MenuItem>}
        {centres.map((c) => (
          <MenuItem key={c.uuid} value={c.uuid}>
            {c.name || c.centre_name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )

  // The BUILDER's picker — which centre this creative is for. It used to be
  // read-only text ("chosen on the dashboard"), which stops working now that the
  // list can be on "all centres" and a new creative inherits nothing.
  const centrePicker = (
    <FormControl variant="outlined" size="small" fullWidth>
      <InputLabel>Centre</InputLabel>
      <Select
        label="Centre"
        value={centreUuid}
        onChange={(e) => setCentreUuid(e.target.value)}
        disabled={openIsFrozen}
      >
        {centres.map((c) => (
          <MenuItem key={c.uuid} value={c.uuid}>
            {c.name || c.centre_name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )

  const infoDialog = (
    <Dialog
      open={infoOpen}
      onClose={() => setInfoOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>How Event Creatives work</DialogTitle>
      <DialogContent>
        <Typography variant="body2" gutterBottom>
          One poster per event — a field trip, a celebration, an Openhouse
          event, or something run with an outside partner. Pick your centre at
          the top right, then <b>New Creative</b>.
        </Typography>
        <ol style={{ paddingLeft: 18, lineHeight: 1.7 }}>
          <li>
            Choose the <b>category</b> — it sets the poster&apos;s look and
            decides which templates are on offer (a field trip has no partner
            and no facilitator, so those templates disappear).
          </li>
          <li>
            Pick a <b>template</b>: standard, external partner (their logo sits
            beside the Openhouse wordmark), or facilitator (photo, name, bio).
          </li>
          <li>
            Fill in the title, the one-line description, and the date, age
            group and venue. What you type appears in the preview as you go.
          </li>
          <li>
            Add the artwork. <b>Upload</b> your own image, or use{' '}
            <b>Generate (AI)</b> to have it drawn for you. If AI generation is
            unavailable, <b>Copy prompt</b> puts the ready-made prompt on your
            clipboard — paste it into Gemini, then upload the result back here.
          </li>
          <li>
            <b>Save</b>, then <b>Image</b> to download the poster as a PNG you
            can send on WhatsApp.
          </li>
        </ol>
        <Typography variant="body2" color="textSecondary">
          <b>Freeze</b> (the padlock on a row) locks a creative for good — it
          can no longer be edited or deleted. Use it once a poster has gone out
          to parents, so the record of what you sent can&apos;t change.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setInfoOpen(false)} color="primary">
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  )

  // The board, as a function, so it can be rendered in TWO places: the
  // builder's preview pane, and an off-screen node on the LIST (download
  // from the list must not drag you into the editor to capture it).
  const renderBoard = () => (
        <div ref={boardRef} className={classes.board}>
          <div
            className={`${classes.headRow} ${
              hasPartner && coBrand ? classes.headRowPartner : ''
            }`}
          >
            {hasPartner && coBrand && (
              <img src={coBrand} alt="" className={classes.coBrand} />
            )}
            <div>
              <img
                src={ohLogo}
                alt="openhouse"
                className={classes.headLogo}
              />
              <p className={classes.headTagline}>raising curious humans</p>
            </div>
          </div>

          <div className={classes.boardBody}>
            {eyebrow.trim() && <p className={classes.eyebrow}>{eyebrow}</p>}
            <p
              className={classes.eventTitle}
              style={titleColor ? { color: titleColor } : undefined}
            >
              {title.trim() || 'Your event title'}
            </p>
            {withLine.trim() && (
              <div className={classes.titleWithRow}>
                <span className={classes.titleWithWord}>
                  with {withLine.trim()}
                </span>
              </div>
            )}

            {subtitle.trim() && (
              <p className={classes.subtitle}>{subtitle}</p>
            )}

            {!!bullets.length && (
              <div className={classes.bullets}>
                {bullets.map((b, i) => (
                  <div
                    /* eslint-disable-next-line react/no-array-index-key */
                    key={i}
                    className={classes.bulletRow}
                  >
                    <span className={classes.sparkle}>✦</span>
                    <span>
                      <span
                        className={classes.bulletLead}
                        style={{ color: b.color }}
                      >
                        {b.lead}
                      </span>
                      {b.rest ? ` ${b.rest}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {metaLine && <p className={classes.metaLine}>📅 {metaLine}</p>}
            {venue.trim() && <p className={classes.venue}>📍 {venue}</p>}
            {footnote.trim() && (
              <p className={classes.footnote}>{footnote}</p>
            )}
          </div>

          {/* the artwork ALWAYS sits at the bottom */}
          <div className={classes.artWrap}>
            {photo ? (
              <img src={photo} alt="" className={classes.photo} />
            ) : (
              <div className={classes.photoEmpty}>
                photo or AI artwork goes here
              </div>
            )}
            {/* dissolve the top edge so there is no line across the poster */}
            {photo && <div className={classes.artFade} />}
          </div>

          {hasHost && (
            <div className={classes.hostBand}>
              <div className={classes.hostRule}>
                <span className={classes.hostRuleLine} />
                Facilitated by
                <span className={classes.hostRuleLine} />
              </div>
              <div className={classes.hostRow}>
                {hostPhoto ? (
                  <img src={hostPhoto} alt="" className={classes.hostPhoto} />
                ) : (
                  <div className={classes.hostPhotoEmpty}>photo</div>
                )}
                <div className={classes.hostInfo}>
                  <p className={classes.hostName}>
                    {hostName.trim() || 'Facilitator name'}
                  </p>
                  {hostRole.trim() && (
                    <p className={classes.hostRole}>{hostRole}</p>
                  )}
                  {hostBio.trim() && (
                    <p className={classes.hostBio}>{hostBio}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
  )


  if (view === 'list') {
    return (
      <Box className={classes.listRoot}>
        <Box className={classes.listHeader}>
          <Box display="flex" alignItems="center" gridGap={4}>
            <Typography variant="h5">Event Creatives</Typography>
            <Tooltip title="How this page works">
              <IconButton size="small" onClick={() => setInfoOpen(true)}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box display="flex" alignItems="center" gridGap={12}>
            {listCentrePicker}
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={openNew}
            >
              New Creative
            </Button>
          </Box>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Creative</TableCell>
              <TableCell>Centre</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Last updated</TableCell>
              <TableCell>Updated by</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {drafts.map((d) => (
              <TableRow key={d.uuid} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gridGap={6}>
                    {d.frozen && (
                      <Tooltip title="Frozen — can no longer be edited or deleted">
                        <LockIcon fontSize="small" color="disabled" />
                      </Tooltip>
                    )}
                    {d.name}
                  </Box>
                </TableCell>
                <TableCell>{d.centre_name || '—'}</TableCell>
                <TableCell>{categoryLabel(d.category)}</TableCell>
                <TableCell>
                  {lastUpdated(d.modified)}
                </TableCell>
                <TableCell>{d.updated_by_name || '—'}</TableCell>
                <TableCell align="right">
                  <Tooltip title={d.frozen ? 'View (frozen)' : 'Edit'}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={loadingDraft}
                        onClick={() => openDraft(d.uuid)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Download image">
                    <span>
                      <IconButton
                        size="small"
                        disabled={loadingDraft || downloading}
                        onClick={() => openDraft(d.uuid, { download: true })}
                      >
                        <CloudDownloadIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {!d.frozen && (
                    <Tooltip title="Freeze (locks this creative for good)">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => freezeDraft(d.uuid)}
                        >
                          <LockOpenIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip
                    title={d.frozen ? 'Frozen — cannot delete' : 'Delete'}
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={!!d.frozen}
                        onClick={() => removeDraft(d.uuid)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {!drafts.length && (
              <TableRow>
                <TableCell colSpan={6} className={classes.empty}>
                  No event creatives yet for this centre — click &quot;New
                  Creative&quot; to make one (field trips, events,
                  celebrations…).
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Download-from-list captures the board HERE, off-screen — it used to
            open the builder, flash the edit view and grab it from there. */}
        {autoDownload ? (
          <div className={classes.hiddenExport} aria-hidden>
            <div ref={scalerRef} className={classes.previewScaler}>
              {renderBoard()}
            </div>
          </div>
        ) : null}
        {infoDialog}
      </Box>
    )
  }

  const hasPartner = template === 'partner'
  const hasHost = template === 'facilitator'
  const bullets = parseBullets(body)
  // "For ages 3-8 · Saturday, 27 June · 11:00 AM" — one centred line, only the
  // parts that were filled in.
  const metaLine = [ages.trim() && `For ages ${ages.trim()}`, dateLine.trim()]
    .filter(Boolean)
    .join('  ·  ')

  return (
    <Box className={classes.root}>
      <Box className={classes.formColumn}>
        <Box mb={2} display="flex" alignItems="center" gridGap={8}>
          <Tooltip title="Back to all creatives (saves your changes)">
            <span>
              <IconButton
                size="small"
                disabled={savingDraft}
                onClick={handleBack}
              >
                <ArrowBackIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Box flex={1} minWidth={0}>
            <Typography variant="h6" noWrap>
              {draftUuid
                ? drafts.find((d) => d.uuid === draftUuid)?.name || 'Creative'
                : 'New creative'}
            </Typography>
          </Box>
          {openIsFrozen && (
            <Tooltip title="This creative is frozen — it can no longer be edited or deleted">
              <LockIcon fontSize="small" color="disabled" />
            </Tooltip>
          )}
          <Tooltip title="Save this creative to the server (named after the title)">
            <span>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={
                  savingDraft ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )
                }
                disabled={savingDraft || !centreUuid || openIsFrozen}
                onClick={saveDraft}
              >
                Save
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Download as a shareable PNG image">
            <span>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={
                  downloading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <CloudDownloadIcon />
                  )
                }
                disabled={downloading}
                onClick={downloadPng}
              >
                Image
              </Button>
            </span>
          </Tooltip>
        </Box>

        <Box className={classes.form}>
          <Box className={classes.formSection}>
            <Typography className={classes.sectionTitle}>Event</Typography>
            {/* The centre lives HERE, not in the header: the header sits in a
                430px form column, and a picker up there squeezed the creative's
                name down to "N..". */}
            <Box className={classes.field}>{centrePicker}</Box>
            <FormControl
              variant="outlined"
              size="small"
              fullWidth
              className={classes.field}
            >
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                value={category}
                onChange={(e) => {
                  const next = e.target.value
                  setCategory(next)
                  // The old sub-type belongs to the old category — keep it only
                  // if it also exists under the new one.
                  setSubType((prev) =>
                    subTypesFor(next).includes(prev) ? prev : ''
                  )
                  setImageStyle(defaultStyleFor(next))
                  // Keep the template only if the new category allows it —
                  // a field trip has no partner and no facilitator.
                  setTemplate((prev) =>
                    templatesFor(next).includes(prev)
                      ? prev
                      : defaultTemplateFor(next)
                  )
                }}
              >
                {CATEGORIES.map((c) => (
                  <MenuItem key={c.key} value={c.key}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              variant="outlined"
              size="small"
              fullWidth
              className={classes.field}
            >
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={subType}
                onChange={(e) => setSubType(e.target.value)}
              >
                <MenuItem value="">
                  <em>Not set</em>
                </MenuItem>
                {subTypesFor(category).map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
                <MenuItem value="other">Other…</MenuItem>
              </Select>
            </FormControl>
            <FormControl
              variant="outlined"
              size="small"
              fullWidth
              className={classes.field}
              disabled={templatesFor(category).length < 2}
            >
              <InputLabel>Template</InputLabel>
              <Select
                label="Template"
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
              >
                {TEMPLATES.filter((t) =>
                  templatesFor(category).includes(t.key)
                ).map((t) => (
                  <MenuItem key={t.key} value={t.key}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              className={classes.field}
              // "Eyebrow" is design jargon — it means nothing to a centre team.
              // Say where the text goes instead.
              label="Line above the title (optional)"
              placeholder="e.g. Community event"
              InputLabelProps={{ shrink: true }}
              size="small"
              variant="outlined"
              fullWidth
              value={eyebrow}
              onChange={(e) => setEyebrow(e.target.value)}
            />
            <Box className={classes.row}>
              <TextField
                className={classes.field}
                label="Title (e.g. Chit-Chaat)"
                size="small"
                variant="outlined"
                fullWidth
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              <FormControl
                variant="outlined"
                size="small"
                className={classes.field}
                style={{ minWidth: 130 }}
              >
                <InputLabel>Title colour</InputLabel>
                <Select
                  label="Title colour"
                  value={titleColor}
                  onChange={(e) => setTitleColor(e.target.value)}
                >
                  {TITLE_COLORS.map((c) => (
                    <MenuItem key={c.key || 'default'} value={c.key}>
                      {c.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TextField
              className={classes.field}
              label="With… (optional, e.g. Shreya Bhartia)"
              size="small"
              variant="outlined"
              fullWidth
              value={withLine}
              onChange={(e) => setWithLine(e.target.value)}
            />
            <TextField
              className={classes.field}
              label="Subtitle (e.g. A morning of fossils and prehistoric play.)"
              size="small"
              variant="outlined"
              fullWidth
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
            />
            <TextField
              className={classes.field}
              label="What they'll do — one per line, first word is highlighted"
              helperText="e.g. “Dig for hidden fossils in the sand pit”"
              size="small"
              variant="outlined"
              fullWidth
              multiline
              minRows={4}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
            <Box className={classes.row}>
              <TextField
                className={classes.field}
                label="Ages (e.g. 3–8)"
                size="small"
                variant="outlined"
                fullWidth
                value={ages}
                onChange={(e) => setAges(e.target.value)}
              />
              <TextField
                className={classes.field}
                label="Date & time"
                size="small"
                variant="outlined"
                fullWidth
                value={dateLine}
                onChange={(e) => setDateLine(e.target.value)}
              />
            </Box>
            <TextField
              className={classes.field}
              label="Venue (e.g. Openhouse HRBR)"
              size="small"
              variant="outlined"
              fullWidth
              value={venue}
              onChange={(e) => setVenue(e.target.value)}
            />
            <TextField
              className={classes.field}
              label="Footnote (orange line at the bottom)"
              size="small"
              variant="outlined"
              fullWidth
              value={footnote}
              onChange={(e) => setFootnote(e.target.value)}
            />
          </Box>

          <Box className={classes.formSection}>
            <Typography className={classes.sectionTitle}>
              Photo / artwork
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Runs full-bleed across the top of the creative. Upload a real
              photo, or generate the artwork.
            </Typography>
            <FormControl
              variant="outlined"
              size="small"
              fullWidth
              className={classes.field}
              style={{ marginTop: 12 }}
            >
              <InputLabel>Artwork style</InputLabel>
              <Select
                label="Artwork style"
                value={imageStyle}
                onChange={(e) => setImageStyle(e.target.value)}
              >
                {IMAGE_STYLES.map((s) => (
                  <MenuItem key={s.key} value={s.key}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box className={classes.row}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<PhotoCameraIcon />}
                onClick={() =>
                  photoInputRef.current && photoInputRef.current.click()
                }
              >
                {photo ? 'Replace' : 'Upload'}
              </Button>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={async (e) => {
                  const file = (e.target.files || [])[0]
                  if (!file) return
                  try {
                    setPhoto(await readFile(file))
                  } catch (err) {
                    showToast('Could not read the image', constants.ERROR)
                  }
                  e.target.value = ''
                }}
              />
              <Tooltip title="Build the artwork prompt and copy it — paste into Gemini, then upload the image back here">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={copying || !title.trim()}
                    startIcon={
                      copying ? <CircularProgress size={14} /> : undefined
                    }
                    onClick={handleCopyPrompt}
                  >
                    {copying ? 'Copying…' : 'Copy prompt'}
                  </Button>
                </span>
              </Tooltip>
              <Button
                size="small"
                variant="outlined"
                disabled={generating || !title.trim()}
                startIcon={
                  generating ? <CircularProgress size={14} /> : undefined
                }
                onClick={handleGenerateArtwork}
              >
                {generating ? 'Generating…' : 'Generate (AI)'}
              </Button>
              {photo && (
                <>
                  <img src={photo} alt="" className={classes.photoThumb} />
                  <IconButton size="small" onClick={() => setPhoto('')}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Box>

            {hasPartner && (
              <>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ display: 'block', marginTop: 16 }}
                >
                  Partner logo (top-left) — for collaborations, e.g. Brik Oven.
                </Typography>
                <Box className={classes.row} mt={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PhotoCameraIcon />}
                    onClick={() =>
                      coBrandInputRef.current && coBrandInputRef.current.click()
                    }
                  >
                    {coBrand ? 'Replace partner logo' : 'Partner logo'}
                  </Button>
                  <input
                    ref={coBrandInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={async (e) => {
                      const file = (e.target.files || [])[0]
                      if (!file) return
                      try {
                        setCoBrand(await readFile(file))
                      } catch (err) {
                        showToast('Could not read the image', constants.ERROR)
                      }
                      e.target.value = ''
                    }}
                  />
                  {coBrand && (
                    <>
                      <img
                        src={coBrand}
                        alt=""
                        className={classes.photoThumb}
                      />
                      <IconButton size="small" onClick={() => setCoBrand('')}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>
              </>
            )}
          </Box>

          {hasHost && (
            <Box className={classes.formSection}>
              <Typography className={classes.sectionTitle}>
                Facilitated by
              </Typography>
              <TextField
                className={classes.field}
                label="Name"
                size="small"
                variant="outlined"
                fullWidth
                value={hostName}
                onChange={(e) => setHostName(e.target.value)}
              />
              <TextField
                className={classes.field}
                label="Role (e.g. Founder · DoughReMom)"
                size="small"
                variant="outlined"
                fullWidth
                value={hostRole}
                onChange={(e) => setHostRole(e.target.value)}
              />
              <TextField
                className={classes.field}
                label="Short bio"
                size="small"
                variant="outlined"
                fullWidth
                multiline
                minRows={3}
                value={hostBio}
                onChange={(e) => setHostBio(e.target.value)}
              />
              <Box className={classes.row}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PhotoCameraIcon />}
                  onClick={() =>
                    hostPhotoInputRef.current &&
                    hostPhotoInputRef.current.click()
                  }
                >
                  {hostPhoto ? 'Replace photo' : 'Facilitator photo'}
                </Button>
                <input
                  ref={hostPhotoInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={async (e) => {
                    const file = (e.target.files || [])[0]
                    if (!file) return
                    setCropping(true)
                    try {
                      const raw = await readFile(file)
                      // The circle crops to a square, and an uploaded photo is
                      // rarely a centred headshot — face-centre it the same way
                      // the roster's ID cards do, or the face lands off to one
                      // side. Falls back to the raw image if the crop fails.
                      let framed = raw
                      try {
                        const res = await faceCropEventCreativePhoto(raw)
                        framed = res?.image || raw
                        if (res && res.faces === 0) {
                          showToast(
                            'No face detected — the photo was centred, check the framing',
                            constants.INFO
                          )
                        }
                      } catch (cropErr) {
                        showToast(
                          'Could not face-centre the photo — using it as-is',
                          constants.INFO
                        )
                      }
                      setHostPhoto(framed)
                    } catch (err) {
                      showToast('Could not read the image', constants.ERROR)
                    } finally {
                      setCropping(false)
                      e.target.value = ''
                    }
                  }}
                />
                {cropping && <CircularProgress size={18} />}
                {hostPhoto && (
                  <>
                    <img
                      src={hostPhoto}
                      alt=""
                      className={classes.photoThumb}
                    />
                    <IconButton size="small" onClick={() => setHostPhoto('')}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <Box className={classes.previewPane} ref={paneRef} p={2}>
        <div
          ref={scalerRef}
          className={classes.previewScaler}
          style={{ transform: `scale(${scale})` }}
        >
          {renderBoard()}
        </div>
      </Box>

      {/* The prompt, on screen. Copy fires from THIS button's own click, so the
          clipboard write is still allowed; if the browser refuses anyway the
          text is selectable and can be copied by hand. */}
      <Dialog
        open={!!promptText}
        onClose={() => setPromptText('')}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Artwork prompt</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Paste this into Gemini, then upload the image it gives you with{' '}
            <b>Replace</b>.
          </Typography>
          <TextField
            multiline
            fullWidth
            minRows={8}
            maxRows={16}
            variant="outlined"
            value={promptText}
            InputProps={{ readOnly: true, style: { fontSize: 13 } }}
            onFocus={(e) => e.target.select()}
          />
        </DialogContent>
        <DialogActions>
          <Button color="primary" variant="contained" onClick={copyPromptToClipboard}>
            Copy
          </Button>
          <Button onClick={() => setPromptText('')}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default EventCreative
